import React, { useState, useEffect } from 'react';
import { api } from '../../src/services/api-new';

interface Order {
    id: number;
    user_id: number;
    customer_name: string;
    customer_email: string;
    total_amount: number;
    status: string;
    created_at: string;
    item_count: number;
    shipping_address: string;
    items?: any[];
}

const AdminOrders: React.FC = () => {
    const [orders, setOrders] = useState<Order[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [activeTab, setActiveTab] = useState<'all' | 'pending' | 'shipped' | 'delivered'>('all');

    const fetchOrders = async () => {
        setIsLoading(true);
        try {
            const data = await api.orders.getAll();
            setOrders(data);
        } catch (error) {
            console.error("Failed to fetch orders:", error);
            alert("Failed to load orders");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchOrders();
    }, []);

    const handleViewOrder = async (order: Order) => {
        try {
            const details = await api.orders.getById(order.id);
            setSelectedOrder(details);
            setShowModal(true);
        } catch (error) {
            console.error("Failed to fetch order details:", error);
            alert("Failed to load order details");
        }
    };

    const handleUpdateStatus = async (status: string) => {
        if (!selectedOrder) return;
        try {
            await api.orders.updateStatus(selectedOrder.id, status);
            alert(`Order #${selectedOrder.id} status updated to ${status}`);
            setShowModal(false);
            fetchOrders(); // Refresh list
        } catch (error) {
            console.error("Failed to update status:", error);
            alert("Failed to update status");
        }
    };

    const filteredOrders = orders.filter(order => {
        if (activeTab === 'all') return true;
        if (activeTab === 'pending') return order.status === 'pending';
        if (activeTab === 'shipped') return order.status === 'In Transit';
        if (activeTab === 'delivered') return order.status === 'Delivered';
        return true;
    });

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'pending': return 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20';
            case 'In Transit': return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
            case 'Delivered': return 'bg-green-500/10 text-green-500 border-green-500/20';
            case 'Cancelled': return 'bg-red-500/10 text-red-500 border-red-500/20';
            default: return 'bg-gray-500/10 text-gray-500';
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
        });
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-4 border-primary/30 border-t-primary rounded-full animate-spin"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-black text-primary-dark dark:text-white tracking-tight">Order Management</h2>
                <button onClick={fetchOrders} className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                    <span className="material-icons-round text-gray-400">refresh</span>
                </button>
            </div>

            {/* Status Tabs */}
            <div className="flex gap-2 overflow-x-auto pb-2">
                {['all', 'pending', 'shipped', 'delivered'].map((tab) => (
                    <button
                        key={tab}
                        onClick={() => setActiveTab(tab as any)}
                        className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${activeTab === tab
                            ? 'bg-primary text-white shadow-lg shadow-primary/20'
                            : 'bg-white dark:bg-surface-dark text-gray-400 hover:text-primary border border-gray-100 dark:border-gray-800'
                            }`}
                    >
                        {tab === 'shipped' ? 'In Transit' : tab}
                    </button>
                ))}
            </div>

            {/* Orders Table */}
            <div className="bg-white dark:bg-surface-dark rounded-[2rem] border border-gray-100 dark:border-gray-800 overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="bg-gray-50/50 dark:bg-white/5 border-b border-gray-100 dark:border-gray-800">
                                <th className="text-left py-5 px-6 text-[10px] font-black text-gray-400 uppercase tracking-widest">Order ID</th>
                                <th className="text-left py-5 px-6 text-[10px] font-black text-gray-400 uppercase tracking-widest">Customer</th>
                                <th className="text-left py-5 px-6 text-[10px] font-black text-gray-400 uppercase tracking-widest">Date</th>
                                <th className="text-left py-5 px-6 text-[10px] font-black text-gray-400 uppercase tracking-widest">Items</th>
                                <th className="text-left py-5 px-6 text-[10px] font-black text-gray-400 uppercase tracking-widest">Total</th>
                                <th className="text-left py-5 px-6 text-[10px] font-black text-gray-400 uppercase tracking-widest">Status</th>
                                <th className="text-right py-5 px-6 text-[10px] font-black text-gray-400 uppercase tracking-widest">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50 dark:divide-gray-800">
                            {filteredOrders.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="py-12 text-center text-gray-400 text-sm font-medium">
                                        No orders found in this category.
                                    </td>
                                </tr>
                            ) : (
                                filteredOrders.map((order) => (
                                    <tr key={order.id} className="hover:bg-gray-50 dark:hover:bg-white/5 transition-colors group">
                                        <td className="py-5 px-6 font-bold text-gray-900 dark:text-white">#{order.id}</td>
                                        <td className="py-5 px-6">
                                            <p className="font-bold text-gray-900 dark:text-white">{order.customer_name || 'Guest User'}</p>
                                            <p className="text-xs text-gray-400">{order.customer_email}</p>
                                        </td>
                                        <td className="py-5 px-6 text-sm text-gray-500 font-medium">{formatDate(order.created_at)}</td>
                                        <td className="py-5 px-6 text-sm font-bold">{order.item_count || 0} items</td>
                                        <td className="py-5 px-6 font-black text-primary-dark dark:text-white">${order.total_amount?.toFixed(2)}</td>
                                        <td className="py-5 px-6">
                                            <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase border ${getStatusColor(order.status)}`}>
                                                {order.status}
                                            </span>
                                        </td>
                                        <td className="py-5 px-6 text-right">
                                            <button
                                                onClick={() => handleViewOrder(order)}
                                                className="w-8 h-8 rounded-lg bg-gray-50 dark:bg-white/5 flex items-center justify-center text-gray-400 hover:text-primary hover:bg-primary/10 transition-all"
                                            >
                                                <span className="material-icons-round text-lg">visibility</span>
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Order Detail Modal */}
            {showModal && selectedOrder && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/40 backdrop-blur-sm" onClick={() => setShowModal(false)}>
                    <div className="bg-white dark:bg-surface-dark w-full max-w-2xl rounded-[2.5rem] p-8 shadow-2xl relative overflow-hidden" onClick={e => e.stopPropagation()}>
                        <div className="flex justify-between items-start mb-6">
                            <div>
                                <h3 className="text-2xl font-black text-primary-dark dark:text-white tracking-tight">Order #{selectedOrder.id}</h3>
                                <p className="text-gray-400 font-bold text-sm mt-1">{formatDate(selectedOrder.created_at)}</p>
                            </div>
                            <button onClick={() => setShowModal(false)} className="w-10 h-10 rounded-full bg-gray-50 hover:bg-gray-100 flex items-center justify-center transition-colors">
                                <span className="material-icons-round">close</span>
                            </button>
                        </div>

                        <div className="grid md:grid-cols-2 gap-8 mb-8">
                            <div>
                                <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3">Customer Details</h4>
                                <div className="bg-gray-50 dark:bg-background-dark/50 p-4 rounded-2xl space-y-1">
                                    <p className="font-bold">{selectedOrder.customer_name || 'Guest'}</p>
                                    <p className="text-sm text-gray-500">{selectedOrder.customer_email}</p>
                                    <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                                        <p className="text-xs text-gray-400 mb-1">Shipping Address:</p>
                                        <p className="text-sm font-medium">
                                            {(() => {
                                                try {
                                                    const addr = JSON.parse(selectedOrder.shipping_address);
                                                    return `${addr.address}, ${addr.city} ${addr.postal_code}`;
                                                } catch {
                                                    return selectedOrder.shipping_address;
                                                }
                                            })()}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3">Order Status</h4>
                                <div className="bg-gray-50 dark:bg-background-dark/50 p-4 rounded-2xl space-y-4">
                                    <div className={`px-4 py-2 rounded-xl text-center border ${getStatusColor(selectedOrder.status)}`}>
                                        <span className="font-black text-xs uppercase tracking-widest">{selectedOrder.status}</span>
                                    </div>

                                    <div className="space-y-2">
                                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Update Status:</p>
                                        <div className="flex gap-2">
                                            {selectedOrder.status !== 'In Transit' && selectedOrder.status !== 'Delivered' && (
                                                <button
                                                    onClick={() => handleUpdateStatus('In Transit')}
                                                    className="flex-1 py-2 bg-blue-500 text-white rounded-xl text-[10px] font-black uppercase tracking-wider hover:bg-blue-600 transition-colors"
                                                >
                                                    Ship Order
                                                </button>
                                            )}
                                            {selectedOrder.status === 'In Transit' && (
                                                <button
                                                    onClick={() => handleUpdateStatus('Delivered')}
                                                    className="flex-1 py-2 bg-green-500 text-white rounded-xl text-[10px] font-black uppercase tracking-wider hover:bg-green-600 transition-colors"
                                                >
                                                    Mark Delivered
                                                </button>
                                            )}
                                            {selectedOrder.status === 'Delivered' && (
                                                <p className="text-center text-xs text-green-600 font-bold w-full py-1">Order Completed</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div>
                            <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3">Order Items</h4>
                            <div className="bg-white dark:bg-surface-dark border border-gray-100 dark:border-gray-800 rounded-2xl overflow-hidden">
                                <table className="w-full text-sm">
                                    <thead className="bg-gray-50/50">
                                        <tr>
                                            <th className="px-4 py-3 text-left font-bold text-gray-500">Product</th>
                                            <th className="px-4 py-3 text-center font-bold text-gray-500">Qty</th>
                                            <th className="px-4 py-3 text-right font-bold text-gray-500">Price</th>
                                            <th className="px-4 py-3 text-right font-bold text-gray-500">Total</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-50">
                                        {selectedOrder.items?.map((item: any, idx: number) => (
                                            <tr key={idx}>
                                                <td className="px-4 py-3 font-medium">{item.product_name || `Item #${item.product_id}`}</td>
                                                <td className="px-4 py-3 text-center text-gray-500">x{item.quantity}</td>
                                                <td className="px-4 py-3 text-right text-gray-500">${item.price}</td>
                                                <td className="px-4 py-3 text-right font-bold">${(item.price * item.quantity).toFixed(2)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot className="bg-gray-50/50">
                                        <tr>
                                            <td colSpan={3} className="px-4 py-3 text-right font-black uppercase tracking-wider text-xs">Total Amount</td>
                                            <td className="px-4 py-3 text-right font-black text-primary text-lg">${selectedOrder.total_amount.toFixed(2)}</td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminOrders;
